import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import { defer, difference, get, set } from 'lodash';
import hash from 'object-hash';
import type { Observer, Subscription } from 'rxjs';
import { Observable, from } from 'rxjs';
import { finalize, map, share, switchMap } from 'rxjs/operators';
import type { Optional } from 'utility-types';

import type { Message } from '@swf/core/types/Notification';

import type { RequestOptions } from '../types/Request';

import { connect } from './connect';
import { request } from './request';
import { subscribe } from './subscribe';

export type RequestStreamData<D> = D & {
  subscriptions: { [path: string]: string };
};

export type BoundRequestStream = <D>(
  options: Optional<RequestOptions, 'meta'>,
  initialData?: RequestStreamData<D>
) => Observable<RequestStreamData<D>>;

const streams = {};

const stream = <D>(
  message: RequestStreamData<D>,
  connection: RequestOptions['connection'],
  resend: boolean
) =>
  new Observable((stream: Observer<typeof message>) => {
    let current = message;
    const subscriptions: { [key: string]: Subscription } = {};

    if (resend) {
      stream.next(message);
    }

    const streamSubscribe = (path: string) =>
      subscribe(current.subscriptions[path], {}, connection).subscribe(({ body: update }) => {
        const oldSubs = current.subscriptions;
        current = produce(current, (draft) => {
          const pathArray = path.substring(1).split('/');
          if (current.subscriptions[path].endsWith('/patches')) {
            applyPatch(get(draft, pathArray), update);
            return;
          }
          if (path === '/') return update;
          set(draft, pathArray, update);
        });
        stream.next(current);
        const newSubs = current.subscriptions || {};

        if (oldSubs !== newSubs) {
          const oldKeys = Object.keys(oldSubs);
          const newKeys = Object.keys(newSubs);
          difference(oldKeys, newKeys).forEach((path) => {
            subscriptions[path].unsubscribe();
            delete subscriptions[path];
          });
          difference(newKeys, oldKeys).forEach(
            (path) => (subscriptions[path] = streamSubscribe(path))
          );
        }
      });

    Object.keys(current.subscriptions || {}).forEach(
      (path) => (subscriptions[path] = streamSubscribe(path))
    );
    return () => Object.values(subscriptions).forEach((sub) => sub.unsubscribe());
  });

export const requestStream = <D>(
  options: Omit<RequestOptions, 'timeout'>,
  initialData?: RequestStreamData<D>
) => {
  const key = hash(options);
  const { connection = connect() } = options;

  if (!streams[key]) {
    const [, , , prefix, ...rest] = options.service.split('/');
    if (prefix === 'stream') {
      const replyTopic = `/user/queue/stream/${rest.join('/')}/${key}`;
      const sub = subscribe<Message<D>>(replyTopic);
      defer(() =>
        request({
          ...options,
          headers: { ...options.headers, 'reply-topic': replyTopic },
          timeout: 1,
          silent: true
        }).catch((err) => undefined)
      );
      streams[key] = sub.pipe(
        map(({ body }) => ({ data: body })),
        share(),
        finalize(() => delete streams[key])
      );
    } else {
      streams[key] = from(
        initialData
          ? [initialData]
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            request<RequestStreamData<D>>(options).then(({ body }): RequestStreamData<D> => body)
      ).pipe(
        switchMap((data: RequestStreamData<D>) => stream<D>(data, connection, !initialData)),
        share(),
        finalize(() => delete streams[key])
      );
    }
  }

  return streams[key] as Observable<RequestStreamData<D>>;
};
